/**
 * useSellerValuation Hook
 * 
 * Manages seller valuation state and API calls
 * Replaces mock computeSellerInsights with real Land Registry data
 */

import { useState, useEffect, useCallback } from 'react';
import { computeValuation } from '../api/sellerOnboarding.js';

/**
 * Hook to compute and manage seller valuation
 * 
 * @param {Object} sellerProfile - Seller profile with address, postcode, beds, baths, propertyType
 * @returns {Object} - { valuation, comparables, loading, error, refetch }
 */
export function useSellerValuation(sellerProfile) {
  const [valuation, setValuation] = useState(null);
  const [comparables, setComparables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchValuation = useCallback(async () => {
    if (!sellerProfile?.id || !sellerProfile?.postcode) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.debug("[useSellerValuation] Fetching valuation from Land Registry data", {
        onboardingId: sellerProfile.id,
        postcode: sellerProfile.postcode,
        bedrooms: sellerProfile.beds
      });

      const response = await computeValuation(
        sellerProfile.id,
        sellerProfile.postcode,
        sellerProfile.beds,
        sellerProfile.propertyType
      );

      if (response.error) {
        setError(response.error);
        setValuation(null);
        setComparables([]);
        console.warn("[useSellerValuation] Insufficient valuation data", { error: response.error });
        return;
      }

      setValuation({
        lower: response.estimateLow,
        upper: response.estimateHigh,
        midpoint: response.estimateMid,
        confidence: Math.round((response.comparables.length / 10) * 100), // 0-100%
        dataSource: response.dataSource,
        comparablesCount: response.comparables.length
      });

      setComparables(response.comparables || []);

      console.debug("[useSellerValuation] Valuation computed successfully", {
        midpoint: response.estimateMid,
        comparablesUsed: response.comparables.length
      });
    } catch (err) {
      console.error("[useSellerValuation] Failed to compute valuation", err);
      setError(err.message || "Failed to compute valuation");
      setValuation(null);
      setComparables([]);
    } finally {
      setLoading(false);
    }
  }, [sellerProfile?.id, sellerProfile?.postcode, sellerProfile?.beds, sellerProfile?.propertyType]);

  useEffect(() => {
    fetchValuation();
  }, [fetchValuation]);

  return {
    valuation,
    comparables,
    loading,
    error,
    refetch: fetchValuation
  };
}

/**
 * Format comparable sale for display
 */
export function formatComparable(comparable) {
  return {
    address: comparable.address,
    postcode: comparable.postcode,
    price: comparable.price,
    bedrooms: comparable.bedrooms,
    propertyType: comparable.propertyType,
    soldAt: new Date(comparable.soldAt).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }),
    pricePerBed: comparable.bedrooms ? Math.round(comparable.price / comparable.bedrooms) : null
  };
}

/**
 * Calculate confidence score explanation
 */
export function getConfidenceExplanation(comparablesCount) {
  if (comparablesCount >= 10) return "High confidence (10+ comparables)";
  if (comparablesCount >= 6) return "Good confidence (6-9 comparables)";
  if (comparablesCount >= 3) return "Moderate confidence (3-5 comparables)";
  return "Low confidence (insufficient comparables)";
}
